import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
import {
  FIJ_INTEREST_STATUS,
  FIJ_JOB_STATUS,
} from '../../../../utils/constants/enums.js';

// https://ant.design/components/table/#components-table-demo-head
// filtersMap param represents object map of value: translation.
export const createDataGridFilters = (filterTranslationsMap) =>
  Object.entries(filterTranslationsMap).map(([value, translation]) => ({
    text: translation,
    value,
  }));

export const useYesNoDataGridFilter = (dataIndex) => {
  const { t } = useTranslation();

  const optionsTranslations = useMemo(
    () => ({
      1: t('Áno'),
      0: t('Nie'),
    }),
    [t],
  );

  // Create basic filter based on antd docs, with Áno first, Nie second.
  return {
    render: (option) => optionsTranslations[option],
    filters: [
      { text: t('Áno'), value: 1 },
      { text: t('Nie'), value: 0 },
    ],
    onFilter: (value, record) => record[dataIndex] === value,
    sorter: (recordA, recordB) => {
      const valueA = get(recordA, dataIndex, false);
      const valueB = get(recordB, dataIndex, false);
      return valueA - valueB;
    },
    width: 150,
  };
};
export const useOptionsDataGridFilter = (dataIndex, options) => {
  const filters = useMemo(
    () =>
      Object.entries(options).map(([value, label]) => ({
        text: label, // filter menu text
        value,
      })),
    [options],
  );

  return {
    filters,
    onFilter: (value, record) => record[dataIndex] === value,
    width: 150,
  };
};

export const useStateFriPraxDataGridFilter = (dataIndex) => {
  const { t } = useTranslation();

  const optionsTranslations = useMemo(
    () => ({
      [FIJ_JOB_STATUS.WAITING]: t(FIJ_JOB_STATUS.WAITING),
      [FIJ_JOB_STATUS.APPROVED]: t(FIJ_JOB_STATUS.APPROVED),
      [FIJ_JOB_STATUS.REJECTED]: t(FIJ_JOB_STATUS.REJECTED),
      [FIJ_JOB_STATUS.DEFENDED]: t(FIJ_JOB_STATUS.DEFENDED),
      [FIJ_JOB_STATUS.UNDEFENDED]: t(FIJ_JOB_STATUS.UNDEFENDED),
    }),
    [t],
  );

  // Create basic filter basend on antd docs.
  return {
    render: (option) => optionsTranslations[option],
    filters: createDataGridFilters(optionsTranslations),
    onFilter: (value, record) => record[dataIndex] === value,
    width: 110,
  };
};

export const useStateFriPraxInterestsDataGridFilter = (dataIndex) => {
  const { t } = useTranslation();

  const optionsTranslations = useMemo(
    () => ({
      [FIJ_INTEREST_STATUS.WAITING]: t(FIJ_INTEREST_STATUS.WAITING),
      [FIJ_INTEREST_STATUS.ACCEPTED]: t(FIJ_INTEREST_STATUS.ACCEPTED),
      [FIJ_INTEREST_STATUS.ACCEPTED_ON_ANOTHER_FIJ]: t(
        FIJ_INTEREST_STATUS.ACCEPTED_ON_ANOTHER_FIJ,
      ),
      [FIJ_INTEREST_STATUS.NOT_ACCEPTED]: t(FIJ_INTEREST_STATUS.NOT_ACCEPTED),
      [FIJ_INTEREST_STATUS.NOT_FINISHED]: t(FIJ_INTEREST_STATUS.NOT_FINISHED),
      [FIJ_INTEREST_STATUS.SUCCESSFULLY_FINISHED]: t(
        FIJ_INTEREST_STATUS.SUCCESSFULLY_FINISHED,
      ),
    }),
    [t],
  );

  // Create basic filter basend on antd docs.
  return {
    render: (option) => optionsTranslations[option],
    filters: createDataGridFilters(optionsTranslations),
    onFilter: (value, record) => record[dataIndex] === value,
    width: 200,
  };
};

export const useSubjectsDataGridFilter = (dataIndex, subjects) => {
  const { t } = useTranslation();
  const optionsSubjects = useMemo(() => {
    if (!Array.isArray(subjects)) return {};

    const result = {};
    subjects.forEach((subject) => {
      if (subject.id !== null) {
        result[subject.id] = `${subject.code} - ${subject.name}`;
      } else {
        result['null'] = subject.name;
      }
    });

    return result;
  }, [subjects]);

  const filters = Object.entries(optionsSubjects).map(([value, text]) => ({
    text,
    value,
  }));

  return {
    filters,
    onFilter: (value, record) => {
      const subject = record[dataIndex];
      const subjectId = subject?.id ?? 'null';
      return subjectId === value;
    },
    render: (subject) => {
      if (!subject || !subject.id) return t('Vlastná udalosť');
      return `${subject.code} - ${subject.name}`;
    },
    width: 250,
  };
};

export const useYesNoStringDataGridFilter = (dataIndex) => {
  const { t } = useTranslation();

  const optionsTranslations = useMemo(
    () => ({
      Yes: t('Áno'),
      No: t('Nie'),
    }),
    [t],
  );

  return {
    filters: createDataGridFilters(optionsTranslations),
    onFilter: (value, record) => record[dataIndex] === value,
  };
};

export const useGuarantorDataGridFilter = (dataIndex, projects) => {
  const guarantorsMap = useMemo(() => {
    if (!Array.isArray(projects)) return {};

    const map = {};
    projects.forEach((project) => {
      const teachers = project[dataIndex] || [];
      const guarantor = teachers.find((t) => t.pivot?.is_guarantor === 'Áno');
      if (
        guarantor?.person_id &&
        guarantor?.person?.full_name &&
        !map[guarantor.person_id]
      ) {
        map[guarantor.person_id] = guarantor.person.full_name;
      }
    });

    return map;
  }, [projects, dataIndex]);

  const filters = Object.entries(guarantorsMap)
    .sort(([, a], [, b]) => a.localeCompare(b))
    .map(([value, text]) => ({
      text,
      value: parseInt(value),
    }));

  return {
    filters,
    onFilter: (value, record) => {
      const teachers = record[dataIndex] || [];
      const guarantor = teachers.find((t) => t.pivot?.is_guarantor === 'Áno');
      return guarantor?.person_id === value;
    },
  };
};

export const useStudyProgramsDataGridFilter = (dataIndex, projects) => {
  const studyProgramsMap = useMemo(() => {
    if (!Array.isArray(projects)) return {};

    const programsMap = {};
    projects.forEach((project) => {
      const programs = project.study_programs || [];
      programs.forEach((program) => {
        if (program.id && !programsMap[program.id]) {
          programsMap[program.id] = program.shortcut;
        }
      });
    });

    return programsMap;
  }, [projects]);

  const filters = Object.entries(studyProgramsMap).map(([value, text]) => ({
    text,
    value: parseInt(value),
  }));

  return {
    filters,
    onFilter: (value, record) => {
      const programs = record[dataIndex] || [];
      return programs.some((program) => program.id === value);
    },
    width: 150,
  };
};
